import type {
  NavigationGuardReturn,
  RouteLocationNormalized,
  Router,
} from "vue-router";

import { UUID_SEGMENT_REGEX } from "@/constants/regex";
import { isEmbedded } from "@/remote/contract";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import {
  adoptCustomerOrganisation,
  adoptMspOrganisation,
} from "@/utils/navigation/organisationNavigation";
import {
  isOrgTier,
  orgLanding,
  orgTierOf,
  type OrgTier,
} from "@/utils/navigation/orgTier";

/**
 * Makes the store follow the URL on any org-scoped navigation titan did not start itself —
 * Back/Forward, a host-driven `popstate`, a pasted link. The layouts read the store, not the
 * route, so without this every panel stays on the previous company. A navigation titan made via
 * `navigateTo*Organisation` sets the store first and passes through untouched.
 */

type OrgTarget = { segment: OrgTier; uuid: string };

type OrgRecord = {
  uuid: string;
  name?: string;
  organisation_type?: string;
  ancestors?: OrgRecord[];
};

const orgTarget = (route: RouteLocationNormalized): OrgTarget | null => {
  const [segment, second] = route.path.split("/").filter(Boolean);
  if (!isOrgTier(segment)) return null;
  const param = route.params.orgUuid;
  const uuid = typeof param === "string" && param ? param : second;
  if (!uuid || !UUID_SEGMENT_REGEX.test(uuid)) return null;
  return { segment, uuid };
};

const sameTarget = (left: OrgTarget | null, right: OrgTarget | null) =>
  left !== null &&
  right !== null &&
  left.segment === right.segment &&
  left.uuid === right.uuid;

const lookupOrg = async (uuid: string): Promise<OrgRecord | null> => {
  try {
    return (await useOrganisationsStore().getOrgWithAncestors({
      uuid,
    })) as OrgRecord | null;
  } catch {
    return null;
  }
};

/** The name titan already holds for this org, when the IDP could not be asked. */
const heldName = (
  store: ReturnType<typeof useAuthenticationStore>,
  uuid: string,
): string =>
  [store.selectedCustomer, store.selectedMsp, store.selectedAccount].find(
    (org) => org?.uuid === uuid,
  )?.name ?? "";

/** The customer's nearest MSP ancestor; `ancestors` runs root → parent. */
const mspAncestor = (org: OrgRecord | null): OrgRecord | null =>
  [...(org?.ancestors ?? [])]
    .reverse()
    .find((ancestor) => orgTierOf(ancestor.organisation_type) === "msp") ??
  null;

const adopt = async (
  tier: OrgTier,
  uuid: string,
  org: OrgRecord | null,
): Promise<void> => {
  const store = useAuthenticationStore();
  const name = org?.name || heldName(store, uuid);

  if (tier === "customer") {
    if (store.selectedCustomer?.uuid !== uuid) {
      await adoptCustomerOrganisation({ id: uuid, name });
    }
    // Branding and `mspGlobalOrganisationId` read `selectedMsp`, so it must be the real parent.
    const msp = mspAncestor(org);
    if (msp && store.selectedMsp?.uuid !== msp.uuid) {
      await store.setMspUser({ uuid: msp.uuid, name: msp.name ?? "" });
    }
    return;
  }

  if (tier === "msp" && store.selectedMsp?.uuid !== uuid) {
    await adoptMspOrganisation({ id: uuid, name });
    return;
  }

  // `msp` already selected, or `system` (SystemLayout adopts the org with its real name itself).
  if (store.selectedCustomer) await store.setCustomer(null);
};

export const orgScopeGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): Promise<NavigationGuardReturn> => {
  const target = orgTarget(to);
  if (!target || sameTarget(target, orgTarget(from))) return true;

  try {
    const org = await lookupOrg(target.uuid);
    const tier = orgTierOf(org?.organisation_type) ?? target.segment;

    if (tier !== target.segment) {
      return { path: orgLanding(tier, target.uuid) };
    }

    await adopt(tier, target.uuid, org);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      `[titan] could not scope the store to ${target.segment} ${target.uuid} — navigating anyway.`,
      error,
    );
  }
  return true;
};

/**
 * EMBEDDED ONLY. Standalone, the layouts scope themselves and titan's own switcher sets the store
 * before it navigates, so the guard would add an idp lookup and a possible tier redirect to every
 * org move for nothing. Only a HOST can move the URL under titan's feet.
 *
 * Registered AFTER the auth guard: vue-router stops at the first redirect, so this never runs for
 * a navigation the auth guard turned away.
 */
export const installOrgScopeGuard = (router: Router): void => {
  if (!isEmbedded()) return;
  router.beforeEach(orgScopeGuard);
};

export default installOrgScopeGuard;
